import * as Sentry from '@sentry/react';
import { getImage } from 'gatsby-plugin-image';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useInView } from 'react-intersection-observer';

import { propTypesDataTestimonialCarousel } from './TestimonialCarousel.proptypes';

import Picture from '../../../../ui/Atoms/Picture';

import { getStaticUrl } from '../../../utils/getStaticUrl';

import { Color } from '../../../constants/Colors';
import * as S from './TestimonialCarousel.styles';

export const renderTestimonials = ({
  hasTitle,
  hasManyTestimonial,
  testimonial,
  handleNextTestimonial,
  handlePrevTestimonial,
}) => {
  const {
    testimonial_carousel_author: author,
    testimonial_carousel_description_author: description,
    testimonial_carousel_quote: quote,
    testimonial_carousel_person: { alt: personAlt, localFile: localPerson },
    testimonial_carousel_logo: { alt: logoAlt, localFile: localLogo },
  } = testimonial || '';

  const personProps = {
    imageObject: getImage(localPerson),
    imageUrl: getStaticUrl(localPerson?.publicURL),
  };

  const logoProps = {
    imageObject: getImage(localLogo),
    imageUrl: getStaticUrl(localLogo?.publicURL),
  };

  return (
    <S.Testimonial>
      <S.IllustrationContainer>
        <S.AuthorPicture>
          <Picture altText={personAlt} {...personProps} radius='50%' />
        </S.AuthorPicture>
        <S.LogoWrapper>
          <Picture altText={logoAlt} {...logoProps} radius='50%' />
        </S.LogoWrapper>
      </S.IllustrationContainer>
      <S.ContentContainer>
        <S.Quote>{quote}</S.Quote>
        <S.Author>{author}</S.Author>
        <S.Job>{description}</S.Job>
      </S.ContentContainer>
      {hasManyTestimonial && (
        <S.ControlsContainer hasTitle={hasTitle}>
          <S.CustomIcon onClick={() => handlePrevTestimonial()}>
            <S.LinearGradient
              xmlns='http://www.w3.org/2000/svg'
              xmlnsXlink='http://www.w3.org/1999/xlink'
            >
              <S.LinearGradient
                id='custom-gradient'
                x1='100%'
                x2='0%'
                y1='0%'
                y2='100%'
              >
                <stop offset='0%' stopColor={Color.BLUE_DODGER} />
                <stop offset='100%' stopColor={Color.BLUE_RIBBON} />
              </S.LinearGradient>
            </S.LinearGradient>
            <FaChevronLeft />
          </S.CustomIcon>
          <S.CustomIcon onClick={() => handleNextTestimonial()}>
            <S.LinearGradient
              xmlns='http://www.w3.org/2000/svg'
              xmlnsXlink='http://www.w3.org/1999/xlink'
            >
              <S.LinearGradient
                id='custom-gradient'
                x1='100%'
                x2='0%'
                y1='0%'
                y2='100%'
              >
                <stop offset='0%' stopColor={Color.BLUE_DODGER} />
                <stop offset='100%' stopColor={Color.BLUE_RIBBON} />
              </S.LinearGradient>
            </S.LinearGradient>
            <FaChevronRight />
          </S.CustomIcon>
        </S.ControlsContainer>
      )}
    </S.Testimonial>
  );
};

export const TestimonialCarouselSlice = ({ slice }) => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  if (!slice) {
    return null;
  }

  const data = slice.primary || {};
  const {
    testimonial_carousel_title: title,
    testimonial_carousel_illustration: { alt, localFile } = {},
  } = data || '';

  const imageProps = {
    imageObject: getImage(localFile),
    imageUrl: getStaticUrl(localFile?.publicURL),
  };

  const items = slice.items || {};

  if (items.length < 1) {
    Sentry.captureMessage(
      'Error: missing required data in TestimonialCarousel slice'
    );
    return null;
  }

  const handleNextTestimonial = () => {
    if (items.length === currentTestimonial + 1) {
      setCurrentTestimonial(0);
    } else {
      setCurrentTestimonial(currentTestimonial + 1);
    }
  };

  const handlePrevTestimonial = () => {
    if (currentTestimonial === 0) {
      setCurrentTestimonial(items.length - 1);
    } else {
      setCurrentTestimonial(currentTestimonial - 1);
    }
  };

  return (
    <S.Wrapper isVisible={true} ref={ref}>
      <S.Title>{title}</S.Title>
      {renderTestimonials({
        hasTitle: title !== null,
        hasManyTestimonial: items.length > 1,
        testimonial: items[currentTestimonial],
        handleNextTestimonial,
        handlePrevTestimonial,
      })}
      <S.IllustrationWrapper>
        <Picture altText={alt} {...imageProps} />
      </S.IllustrationWrapper>
    </S.Wrapper>
  );
};

TestimonialCarouselSlice.propTypes = {
  /**
   * Content of the TestimonialCarousel slice.
   */
  slice: PropTypes.shape(propTypesDataTestimonialCarousel),
};
